import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { to: "/", label: "首页", end: true },
  { to: "/movie", label: "电影", end: false },
  { to: "/tv", label: "电视剧", end: false },
  { to: "/about", label: "关于", end: false },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?keyword=${encodeURIComponent(kw)}` : "/search");
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? "bg-background-950/95 backdrop-blur border-b border-background-800" : "bg-transparent"
      }`}
    >
      <div className="flex h-16 md:h-20 items-center justify-between px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="免费播放片大片 首页">
          <span className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-lg bg-primary-500 text-foreground-50">
            <i className="ri-movie-2-fill text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base md:text-lg font-bold text-foreground-50 whitespace-nowrap">
              免费播放片大片
            </span>
            <span className="text-[10px] md:text-xs text-accent-400 tracking-wide whitespace-nowrap">
              海量影视 · 免费浏览
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `rounded-md px-3.5 py-2 text-sm whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-accent-400"
                    : "text-foreground-200 hover:text-accent-400 hover:bg-background-800/60"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:flex items-center" role="search">
            <div className="flex items-center rounded-md border border-background-700 bg-background-900/80 px-3 py-1.5 focus-within:border-primary-500">
              <i className="ri-search-line text-sm text-foreground-400"></i>
              <input
                type="search"
                name="keyword"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视片名"
                aria-label="搜索影视"
                className="w-40 bg-transparent px-2 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
              />
            </div>
            <button
              type="submit"
              className="ml-2 flex items-center gap-1 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-background-950 whitespace-nowrap hover:bg-accent-400 transition-colors"
            >
              <i className="ri-search-line"></i>
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-100 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-800 bg-background-950/98 px-4 pb-5 pt-4">
          <form onSubmit={submitSearch} role="search" className="mb-4 flex items-center gap-2">
            <div className="flex flex-1 items-center rounded-md border border-background-700 bg-background-900/80 px-3 py-2">
              <i className="ri-search-line text-sm text-foreground-400"></i>
              <input
                type="search"
                name="keyword"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视片名"
                aria-label="搜索影视"
                className="w-full bg-transparent px-2 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
              />
            </div>
            <button
              type="submit"
              className="rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-950 whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-md px-3 py-2.5 text-sm ${
                    isActive ? "bg-primary-500/15 text-accent-400" : "text-foreground-200 hover:bg-background-800/60"
                  }`
                }
              >
                <i className="ri-arrow-right-s-line text-accent-400"></i>
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}